import React, { useEffect, useState, useRef, useCallback } from 'react';
import { CheckCircle, Download, Share2, Music } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import ApiClient from '../api/client';

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const orderRef = useRef(null);

  // Keep ref in sync with state
  useEffect(() => {
    orderRef.current = order;
  }, [order]);

  const loadOrder = useCallback(async () => {
    if (!orderId) return;
    
    try {
      const orderData = await ApiClient.getOrderStatus(orderId);
      setOrder(orderData);
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error('Error loading order:', err);
      setLoading(false);
      setError(err.message || 'Failed to load order');
    }
  }, [orderId]);

  useEffect(() => {
    if (orderId) {
      loadOrder();
      // Poll for updates every 5 seconds (only if order is not completed or failed)
      const interval = setInterval(() => {
        const currentOrder = orderRef.current;
        if (currentOrder && currentOrder.status !== 'completed' && currentOrder.status !== 'failed') {
          loadOrder();
        } else if (!currentOrder) {
          // If no order yet, keep trying
          loadOrder();
        }
      }, 5000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [orderId, loadOrder]);

  if (!orderId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Order not found</h1>
          <a href="/" className="text-purple-600 hover:underline">Return to home</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 py-16">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          {error ? (
            <>
              <div className="text-center">
                <h1 className="text-2xl font-bold mb-4 text-red-600">Error Loading Order</h1>
                <p className="text-gray-600 mb-4">{error}</p>
                <button
                  onClick={loadOrder}
                  className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </>
          ) : loading ? (
            <>
              <div className="text-center">
                <h1 className="text-3xl font-bold mb-4">Loading your order...</h1>
                <div className="animate-spin w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full mx-auto" />
              </div>
            </>
          ) : order?.status === 'completed' ? (
            <>
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
              <h1 className="text-4xl font-bold text-center mb-4">Your Song is Ready!</h1>
              <p className="text-gray-600 text-center mb-8">
                Check your email for the download link, or download it directly below.
              </p>

              {order.lyrics && (
                <div className="bg-gray-50 p-6 rounded-xl mb-6">
                  <h3 className="font-bold mb-3">Lyrics:</h3>
                  <p className="whitespace-pre-line text-gray-700">{order.lyrics}</p>
                </div>
              )}

              {order.generated_file_url && (
                <div className="space-y-4">
                  <a
                    href={order.generated_file_url}
                    download
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl font-bold hover:shadow-xl transition-all flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Download Song (MP3)
                  </a>

                  <button
                    onClick={() => {
                      if (navigator.share) {
                        navigator.share({
                          title: 'My Personalized Birthday Song',
                          url: order.generated_file_url
                        });
                      }
                    }}
                    className="w-full bg-blue-500 text-white py-4 rounded-xl font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Share2 className="w-5 h-5" />
                    Share Song
                  </button>
                </div>
              )}
            </>
          ) : order?.status === 'processing' || order?.status === 'pending' ? (
            <>
              <div className="animate-spin w-20 h-20 border-4 border-purple-200 border-t-purple-600 rounded-full mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-center mb-4">Creating Your Song...</h1>
              <p className="text-gray-600 text-center mb-8">
                Our AI is composing personalized lyrics and generating your unique song.
                This usually takes 5-10 minutes.
              </p>
              <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6">
                <p className="text-center text-purple-700 font-semibold">
                  Order #{orderId.substring(0, 8)}
                </p>
                <p className="text-center text-sm text-gray-600 mt-2">
                  {order.payment_status === 'paid' 
                    ? "You'll receive an email when it's ready!"
                    : "Waiting for payment confirmation..."}
                </p>
              </div>
            </>
          ) : order?.status === 'failed' ? (
            <>
              <div className="text-center">
                <h1 className="text-2xl font-bold mb-4 text-red-600">Order Failed</h1>
                <p className="text-gray-600 mb-4">
                  There was an issue processing your order. Please contact support.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="text-center">
                <h1 className="text-2xl font-bold mb-4">Order Status Unknown</h1>
                <p className="text-gray-600 mb-4">
                  Unable to determine order status. Please try refreshing the page.
                </p>
                <button
                  onClick={loadOrder}
                  className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Refresh
                </button>
              </div>
            </>
          )}

          <div className="mt-8 text-center">
            <a href="/" className="text-purple-600 hover:underline">
              Create another song
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
